import React, { useCallback, useEffect, useRef, useState } from "react";
import { Meta } from "../layout/Meta";
import Link from "../components/Link";
import BillingAuthPanel from "../components/BillingAuthPanel";
import { useBillingAuth } from "../billing/useBillingAuth";
import { createCheckoutSession, createPortalSession, fetchEntitlement } from "../billing/billingApiClient";
import { navigateAllowlistedBillingUrl } from "../billing/redirectAllowlist";
import {
  DISPLAY_PLANS,
  formatOfferPrice,
  newCheckoutOfferForPlan,
  paidPlanIds,
} from "../billing/planCatalog";
import {
  billingWebConfig,
  isBillingApiConfigured,
  isPublicWebCheckoutEnabled,
} from "../billing/billingConfig";
import { LEGAL_RELATED_LINKS, LEGAL_SHARED } from "../config/legal";
import { SITE } from "../config/site";
import { adaptEntitlement } from "../account/entitlementAdapter";

function Card({ className = "", children }) {
  return (
    <div
      className={[
        "rounded-2xl border border-ink/8 bg-white backdrop-blur-xl",
        "shadow-card",
        className,
      ].join(" ")}
    >
      {children}
    </div>
  );
}

function PlanCard({ plan, offer, isCurrent, paidAccess, busy, signedIn, checkoutEnabled, onUpgrade, onManageBilling, onSignInHint }) {
  return (
    <article
      className="flex h-full flex-col rounded-2xl border border-ink/8 bg-bg1 p-6"
      aria-labelledby={`plan-${plan.planId}-title`}
    >
      <div className="flex items-start justify-between gap-3">
        <h2 id={`plan-${plan.planId}-title`} className="text-xl font-semibold tracking-tight text-ink">
          {plan.displayName}
        </h2>
        {isCurrent ? (
          <span className="rounded-full px-2 py-1 text-xs font-semibold text-ink/80 ring-1 ring-ink/10">Current</span>
        ) : null}
      </div>

      <div className="mt-4">
        {plan.planId === "free" ? (
          <p className="text-2xl font-semibold text-ink">$0</p>
        ) : offer ? (
          <>
            <p className="text-2xl font-semibold tracking-tight text-ink">{formatOfferPrice(offer)}</p>
            <p className="mt-1 text-sm text-muted">Renews every month until cancelled</p>
          </>
        ) : (
          <p className="text-2xl font-semibold text-ink">—</p>
        )}
      </div>

      <ul className="mt-5 flex-1 space-y-2 text-sm leading-relaxed text-muted">
        <li>Cloud Paper Bots (simulated): {plan.limits.effectiveActivePaperBots}</li>
        <li>Extra saved strategies: {plan.limits.extraSavedStrategies}</li>
        <li>Watchlist symbols: {plan.limits.watchlistSymbols}</li>
        <li>Discovery runs / UTC day: {plan.limits.discoveryRunsPerUtcDay}</li>
        <li>Optimizer variants / run: {plan.limits.optimizerVariantsPerRun}</li>
        <li>Active company event rules: {plan.limits.activeCompanyEventRules}</li>
        <li>{plan.capabilities.includes("broker.read_only.connect") ? "Read-only brokerage connection" : "Brokerage connection requires Pro"}</li>
        <li>{plan.capabilities.includes("discovery.full_power") ? "Full Power Discovery" : plan.capabilities.includes("discovery.advanced") ? "Advanced Discovery" : "Basic Discovery"}</li>
        <li>{plan.capabilities.includes("event.company.full") ? "Full company and market event suite" : "Basic company events"}</li>
      </ul>

      {plan.planId !== "free" && !isCurrent ? (
        <div className="mt-6 space-y-2">
          {!signedIn ? (
            <button
              type="button"
              onClick={onSignInHint}
              className="inline-flex min-h-11 w-full items-center justify-center rounded-xl px-4 text-sm font-semibold text-ink/80 ring-1 ring-ink/10 hover:bg-bg1"
            >
              Sign in to subscribe
            </button>
          ) : null}
          <button
            type="button"
            disabled={busy || !offer || !checkoutEnabled || !signedIn}
            onClick={() => offer && onUpgrade(offer.offerKey)}
            className="inline-flex min-h-11 w-full items-center justify-center rounded-xl bg-ink px-4 text-sm font-semibold text-white disabled:opacity-50"
          >
            {busy ? "Working…" : paidAccess ? "Change plan" : `Subscribe to ${plan.displayName}`}
          </button>
        </div>
      ) : null}
    </article>
  );
}

export default function StratifyProPage() {
  const path = "/pricing";
  const title = "Stratify pricing — Free, Pro, and Elite";
  const description =
    "Compare Free, Stratify Pro, and Stratify Elite with USD monthly pricing. Cloud Paper Bots are simulated. Automated live trading is not included.";

  const { user, ready, signedIn, uid } = useBillingAuth();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [messageTone, setMessageTone] = useState("info");
  const [entitlementPayload, setEntitlementPayload] = useState(null);
  const [showAuth, setShowAuth] = useState(false);
  const idempotencyRef = useRef(null);
  const inFlightRef = useRef(false);
  const checkoutEnabled = isPublicWebCheckoutEnabled();
  const sandboxLane = billingWebConfig.sandboxLane === true;

  const adapted = adaptEntitlement(entitlementPayload, { ownerUid: uid });
  const currentPlan = adapted.freshness === "ready" ? String(adapted.plan || "").toLowerCase() : null;
  const paidAccess =
    adapted.freshness === "ready" &&
    (adapted.plan === "pro" || adapted.plan === "elite") &&
    (adapted.accessState === "active" || adapted.accessState === "grace");
  const manageTarget = String(adapted.manageTarget || "none");

  const refreshEntitlement = useCallback(async () => {
    if (!signedIn || !isBillingApiConfigured()) return;
    try {
      const result = await fetchEntitlement();
      setEntitlementPayload({
        status: result.status,
        entitlement: result.entitlement,
        uxGates: result.uxGates,
        correlationId: result.correlationId,
      });
    } catch {
      setEntitlementPayload({ status: "unknown", entitlement: null });
    }
  }, [signedIn]);

  useEffect(() => {
    if (ready && signedIn) {
      refreshEntitlement();
    } else {
      setEntitlementPayload(null);
    }
  }, [ready, signedIn, refreshEntitlement]);

  function setStatus(text, tone = "info") {
    setMessage(text);
    setMessageTone(tone);
  }

  async function onUpgrade(offerKey) {
    if (!checkoutEnabled) {
      setStatus("Website Checkout is not open for public purchases yet.", "info");
      return;
    }
    if (!offerKey || inFlightRef.current) return;
    inFlightRef.current = true;
    setBusy(true);
    setStatus(null);

    if (!idempotencyRef.current) {
      idempotencyRef.current =
        typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `web-${Date.now()}`;
    }

    try {
      const result = await createCheckoutSession({
        offerKey,
        idempotencyKey: idempotencyRef.current,
      });

      if (result.result === "manage_existing_subscription") {
        idempotencyRef.current = null;
        setStatus("You already have an active Stripe subscription. Use Manage subscription.", "info");
        return;
      }
      if (result.result === "active_subscription_other_source") {
        idempotencyRef.current = null;
        const source = String(result.source || "");
        if (source === "app_store" || manageTarget === "apple_subscriptions" || adapted.appleManaged) {
          setStatus(
            "Your subscription is managed through Apple. Open iOS Settings → Apple ID → Subscriptions. Do not start a second Stripe subscription.",
            "info"
          );
        } else {
          setStatus("An active subscription already exists from another source. Contact support if you need help.", "info");
        }
        return;
      }

      if (result.result === 'subscription_already_active') {
        await refreshEntitlement();
        setStatus('Your requested plan is active.');
        idempotencyRef.current = null;
        return;
      }
      if (result.checkoutUrl || result.portalUrl) {
        const ok = navigateAllowlistedBillingUrl(result.checkoutUrl || result.portalUrl);
        if (!ok) {
          setStatus("Checkout redirect was blocked for safety. Please try again or contact support.", "error");
          idempotencyRef.current = null;
        }
        return;
      }

      setStatus("Unexpected Checkout response. Please try again.", "error");
      idempotencyRef.current = null;
    } catch (err) {
      if (err?.code === "signed_out" || err?.status === 401) {
        setShowAuth(true);
        setStatus("Your session expired. Please sign in again.", "error");
      } else if (err?.code === "billing_api_unconfigured") {
        setStatus("The payment service is unavailable.", "error");
      } else {
        setStatus(err?.message || "Unable to start Checkout.", "error");
      }
      idempotencyRef.current = null;
    } finally {
      inFlightRef.current = false;
      setBusy(false);
    }
  }

  async function onManageBilling() {
    if (inFlightRef.current) return;
    if (!checkoutEnabled) {
      setStatus(
        manageTarget === "apple_subscriptions"
          ? "Manage this subscription in Apple Subscriptions."
          : "Website billing management is not open yet. Apple purchases are managed in Apple Subscriptions.",
        "info"
      );
      if (manageTarget === "apple_subscriptions") {
        window.open(billingWebConfig.appleSubscriptionsUrl, "_blank", "noopener,noreferrer");
      }
      return;
    }
    inFlightRef.current = true;
    setBusy(true);
    try {
      const result = await createPortalSession();
      if (result.result === "active_subscription_other_source") {
        const target = String(result.manageTarget || manageTarget);
        if (target === "apple_subscriptions") {
          setStatus("Manage your subscription in Apple Subscriptions.", "info");
          window.open(billingWebConfig.appleSubscriptionsUrl, "_blank", "noopener,noreferrer");
        } else {
          setStatus("This subscription is not managed via Stripe. Contact support.", "info");
        }
        return;
      }
      if (result.portalUrl) {
        const ok = navigateAllowlistedBillingUrl(result.portalUrl);
        if (!ok) setStatus("Portal redirect was blocked for safety.", "error");
      }
    } catch (err) {
      if (err?.code === "signed_out" || err?.status === 401) {
        setShowAuth(true);
        setStatus("Your session expired. Please sign in again.", "error");
      } else if (err?.code === "billing_api_unconfigured") {
        setStatus("The payment service is unavailable.", "error");
      } else {
        setStatus(err?.message || "Unable to open billing portal.", "error");
      }
    } finally {
      inFlightRef.current = false;
      setBusy(false);
    }
  }

  const renewalLabel = "Renews every month until cancelled.";

  return (
    <>
      <Meta title={title} description={description} path={path} />

      <div className="mx-auto w-full max-w-6xl px-5 py-10 md:px-8">
        <Card className="relative overflow-hidden p-6 md:p-8">
          <div
            aria-hidden
            className="pointer-events-none absolute -inset-14 bg-[radial-gradient(circle_at_20%_20%,rgba(85,224,218,0.16),transparent_55%),radial-gradient(circle_at_80%_30%,rgba(44,150,153,0.14),transparent_60%)]"
          />
          <div className="relative">
            <p className="text-xs font-semibold uppercase tracking-wide text-hint-deep/80">Simulation only</p>
            <p className="mt-2 text-sm leading-relaxed text-muted">{LEGAL_SHARED.noLiveTrading}</p>
            {sandboxLane ? (
              <p
                role="status"
                className="mt-4 rounded-2xl border border-ink/8 bg-bg1 px-4 py-3 text-sm leading-relaxed text-muted"
              >
                Local staging sandbox only. Stripe test mode. Do not use a real card. Open this page at
                http://localhost:3000 — http://127.0.0.1 is blocked by the staging API.
              </p>
            ) : null}

            <h1 id="pricing-h1" className="mt-5 text-3xl font-semibold tracking-tight text-ink md:text-5xl">
              Free, Stratify Pro, and Stratify Elite
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-muted md:text-base">
              New website subscriptions are billed monthly in USD.
              {` ${renewalLabel} `}
              Applicable tax may be added at Checkout based on your location. Catalog prices exclude tax unless Checkout
              itemizes tax.
            </p>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-muted">
              {sandboxLane
                ? "This local build talks to the staging Billing API and Stripe test mode. Production Checkout stays closed."
                : "Website Checkout is not open for public purchases yet. The App Store remains the current paid path when Apple in-app purchases are available. Seller of record: Apple for App Store purchases; Stratify Analytics Inc. via Stripe for website purchases."}
            </p>

            <p className="mt-8 text-sm font-medium text-ink/80" role="status">
              {renewalLabel} Applicable tax may be added at Checkout based on your location.
            </p>

            <div className="mt-6 rounded-2xl border border-ink/8 bg-bg1 p-5" aria-labelledby="checkout-disclosure-heading">
              <h2 id="checkout-disclosure-heading" className="text-base font-semibold text-ink">
                Before Checkout
              </h2>
              <ul className="mt-3 space-y-2 text-sm leading-relaxed text-muted">
                <li>
                  Monthly billing · USD
                </li>
                <li>
                  Stratify Pro: {formatOfferPrice(newCheckoutOfferForPlan("pro"))}
                </li>
                <li>
                  Stratify Elite: {formatOfferPrice(newCheckoutOfferForPlan("elite"))}
                </li>
                <li>
                  {renewalLabel} Cancel at period end from Manage subscription (Stripe) or Apple Subscriptions (App
                  Store).
                </li>
                <li>
                  {sandboxLane
                    ? "Sandbox Checkout is enabled in this local build only. Production remains closed."
                    : "Website Checkout is not open for public purchases until Stratify enables it."}
                </li>
              </ul>
              <div className="mt-4 flex flex-wrap gap-3">
                {LEGAL_RELATED_LINKS.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="text-sm font-semibold text-hint-deep underline underline-offset-4 hover:text-ink"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>

            {signedIn && paidAccess ? (
              <div className="mt-6">
                <button
                  type="button"
                  disabled={busy}
                  onClick={onManageBilling}
                  className="inline-flex min-h-11 items-center rounded-xl px-4 text-sm font-semibold text-ink/80 ring-1 ring-ink/10 disabled:opacity-60"
                >
                  Manage subscription
                </button>
              </div>
            ) : null}

            {message ? (
              <p
                role={messageTone === "error" ? "alert" : "status"}
                className={["mt-4 text-sm", messageTone === "error" ? "text-red-300" : "text-muted"].join(" ")}
              >
                {message}
              </p>
            ) : null}

            <div className="mt-8">{showAuth || !signedIn ? <BillingAuthPanel user={user} /> : <BillingAuthPanel user={user} compact />}</div>
          </div>
        </Card>

        <h2 id="plans-heading" className="sr-only">
          Plans
        </h2>
        <div className="mt-8 grid gap-5 md:grid-cols-3">
          {["free", ...paidPlanIds()].map((planId) => {
            const plan = DISPLAY_PLANS[planId];
            const offer = planId === "free" ? null : newCheckoutOfferForPlan(planId);
            return (
              <PlanCard
                key={planId}
                plan={plan}
                offer={offer}
                isCurrent={Boolean(currentPlan) && currentPlan === planId && (planId === "free" || paidAccess)}
                paidAccess={paidAccess}
                busy={busy}
                signedIn={signedIn}
                checkoutEnabled={checkoutEnabled}
                onUpgrade={onUpgrade}
                onManageBilling={onManageBilling}
                onSignInHint={() => setShowAuth(true)}
              />
            );
          })}
        </div>

        <div className="mt-10 flex flex-wrap gap-3">
          {LEGAL_RELATED_LINKS.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="inline-flex min-h-11 items-center justify-center rounded-xl border border-ink/8 bg-bg1 px-4 py-2.5 text-sm font-semibold text-ink/80 hover:bg-bg1"
            >
              {item.label}
            </Link>
          ))}
          <a
            href={SITE.appStoreUrl}
            className="inline-flex min-h-11 items-center justify-center rounded-xl border border-ink/8 bg-bg1 px-4 py-2.5 text-sm font-semibold text-ink/80 hover:bg-bg1"
            target="_blank"
            rel="noopener noreferrer"
          >
            App Store
          </a>
        </div>
      </div>
    </>
  );
}
